<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { updateSelectedIndustryId } from '@/api/user';
import { DgrIcon, DgrLoading, DgrSelectbox } from '@stockmarkteam/donguri-ui';
import AdpDocumentCardSmall from '@/components/common/article/adp-document-card-small.vue';
import { useHomeSectionViewTracking } from '@/components/home/use-home-section-view-tracking';
import { STATES, useIndustries, useIndustryArticles } from '@/utils/swr';
import { useEmitter, useStore } from '@/utils/vue';
import Divider from '../common/divider.vue';

const layoutVariant = 'next';

const store = useStore();
const emitter = useEmitter();
const { data: industriesResponse } = useIndustries();
const feedType = computed(() => store.state.feedType.feedType);

const maxVisible = 3;
const perTopicLimit = 2;

const industries = computed(() => {
  return industriesResponse.value?.industries ?? [];
});

const userInfo = computed(() => store.state.userInfo.userInfo);

const currentIndustry = computed(() =>
  industries.value.find(i => i.id === userInfo.value?.industry_id),
);
const currentIndustryId = ref<number | undefined>(undefined);

watch(
  [industries, userInfo, currentIndustry],
  () => {
    if (currentIndustry.value) {
      currentIndustryId.value = currentIndustry.value.id;
      return;
    }
    if (userInfo.value && industries.value.length > 0) {
      currentIndustryId.value = industries.value[0].id;
    }
  },
  {
    immediate: true,
  },
);

const industryOptions = computed(() =>
  industries.value.map(i => ({ label: i.name, value: i.id })),
);

const {
  data: industryArticlesResponse,
  state,
  mutate: updateIndustryArticles,
} = useIndustryArticles(currentIndustryId, perTopicLimit);

const adpDocuments = computed(
  () => industryArticlesResponse.value?.industry_articles ?? [],
);

const orderedDocuments = computed(() => {
  const documentsByTopic = new Map<number, typeof adpDocuments.value>();
  for (const document of adpDocuments.value) {
    documentsByTopic.set(document.topic_id, [
      ...(documentsByTopic.get(document.topic_id) ?? []),
      document,
    ]);
  }
  const topicDocumentLists = [...documentsByTopic.values()];

  const result: typeof adpDocuments.value = [];
  for (let rank = 0; rank < perTopicLimit; rank++) {
    for (const list of topicDocumentLists) {
      if (list[rank]) result.push(list[rank]);
    }
  }
  return result;
});

const uniqueDocuments = computed(() => {
  const idDeduped = orderedDocuments.value.filter(
    (a, i) => orderedDocuments.value.findIndex(b => b.id === a.id) === i,
  );
  return idDeduped.filter(
    (a, i) => idDeduped.findIndex(b => b.topic_id === a.topic_id) === i,
  );
});

const visibleAdpDocuments = computed(() =>
  uniqueDocuments.value.slice(0, maxVisible),
);

const isLoading = computed(() => state.value === STATES.PENDING);

const handleIndustrySelect = async (industryId: number | undefined) => {
  if (industryId === undefined) return;

  await updateSelectedIndustryId(industryId);
  store.commit('userInfo/setUserIndustryId', industryId);
};

const handleArticleUpdate = () => updateIndustryArticles();
onMounted(() => emitter.on('article-updated', handleArticleUpdate));
onUnmounted(() => emitter.off('article-updated', handleArticleUpdate));

const { setRef, requestId } = useHomeSectionViewTracking(
  'industry_news_view',
  () => !isLoading.value,
  () => ({
    document_count: visibleAdpDocuments.value.length,
    layout_variant: layoutVariant,
  }),
);
</script>

<template>
  <div
    class="industry-news aconnect-home-component"
    v-if="industries.length > 0"
    :ref="setRef"
  >
    <div class="title">
      <DgrIcon
        name="chart-trend"
        :keep-fill="false"
        class="aconnect-home-icon animated icon"
      />
      <router-link :to="{ name: 'industryNews' }" class="c-title-bold--xm"
        >業界・他社の動き</router-link
      >
    </div>
    <div class="c-text-regular--s subtitle">
      AIが業界ごとのおすすめニュースをピックアップしてお届けします。
    </div>
    <div class="industry-select">
      <div class="c-title-medium--m">業界</div>
      <DgrSelectbox
        :options="industryOptions"
        v-model="currentIndustryId"
        size="small"
        @update:model-value="handleIndustrySelect"
      />
      <DgrIcon
        v-if="feedType === 'foreign'"
        name="info"
        size="small"
        class="info-icon"
        :keep-fill="false"
        v-tooltip="{
          content:
            '本機能は国内メディアのみ対応のため、それに該当する記事を表示しています。',
          theme: 'small',
        }"
      />
    </div>
    <Divider class="full-bleed" margin="16px -16px" />
    <div class="loading" v-if="isLoading">
      <DgrLoading />
    </div>
    <template v-else-if="visibleAdpDocuments.length > 0">
      <template
        v-for="(document, index) in visibleAdpDocuments"
        :key="document.id"
      >
        <AdpDocumentCardSmall
          :adp-document="document"
          :rank="index + 1"
          page-name="home"
          feature="industry_news"
          show-published-date
          show-option-menu
          :trend-company-text="document.trend_company_text"
          class="document"
          :contents-context="{
            event_name: 'industry_news',
            parent_request_id: requestId,
          }"
        ></AdpDocumentCardSmall>
        <Divider class="full-bleed" margin="16px -16px" />
      </template>
    </template>
    <div class="empty" v-else>
      <img class="empty-img" src="@/assets/state-empty-article.png" />
      <div class="c-title-medium--xm">この業界のニュースはありませんでした</div>
      <div class="c-text-regular--s">次回の更新は明日7:00です。</div>
    </div>
    <div class="footer">
      <router-link
        :to="{ name: 'industryNews' }"
        class="c-outlineBtn c-outlineBtn--small c-outlineBtn--auto view-more"
      >
        業界・他社の動きをすべて見る
      </router-link>
    </div>
  </div>
</template>

<style scoped lang="scss">
.industry-news {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 12px 16px 0;
  border: 1px solid $color-gray400;
  border-radius: 4px;
  background: #ffffff;

  .full-bleed {
    width: auto;
  }

  .title {
    display: flex;
    align-items: center;
    gap: 4px;

    a:hover {
      color: $color-green600;
      text-decoration: underline;
    }

    .icon {
      color: $color-green600;
    }
  }

  .subtitle {
    margin-top: 12px;
    color: $color-gray800;
  }

  .industry-select {
    display: flex;
    gap: 8px;
    align-items: center;
    margin-top: 12px;

    .icon-box {
      color: $color-gray800;
    }
  }

  .document:has(.document-info-image a:hover) + .divider {
    border-top-color: $color-gray600;
  }
  .divider:has(+ .document .document-info-image a:hover) {
    border-top-color: $color-gray600;
  }

  .view-more {
    margin: 16px auto;

    &:hover {
      background: $color-gray400;
    }
  }

  .loading {
    display: flex;
    justify-content: center;
    padding: 16px 0;
  }

  .empty {
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: center;
    align-items: center;

    .empty-img {
      width: 132px;
      height: 80px;
      object-fit: cover;
    }
    .c-title-medium--xm {
      margin-bottom: 24px;
    }
  }

  .footer {
    margin-top: auto;
  }
}
</style>
